// @ts-strict-ignore
import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { useEmptyColumn } from "@dashboard/components/Datagrid/hooks/useEmptyColumn";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import { SampleRequestListUrlSortField } from "../../urls";
import { ListProps, RelayToFlat, SortPage } from "@dashboard/types";
import { Box } from "@saleor/macaw-ui-next";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { sampleRequestListStaticColumnAdapter, useGetCellContent } from "./datagrid";
import { messages } from "./messages";
import { canBeSorted, getColumnNameAndId, getSampleRequestRowsLength } from "./utils";

// Type based on your GraphQL query
type SampleRequestListQuery = {
  sampleRequests: {
    edges: Array<{
      node: {
        id: string;
        createdAt: string;
        address?: {
          id: string;
          firstName?: string;
          lastName?: string;
          streetAddress1?: string;
          city?: string;
          postalCode?: string;
          country?: {
            code: string;
          };
        };
      };
    }>;
  };
};

const DEFAULT_COLUMNS = ["date", "customer", "address", "city", "postalCode", "country", "action"];

interface SampleRequestListDatagridProps
  extends ListProps,
    SortPage<SampleRequestListUrlSortField> {
  sampleRequests: RelayToFlat<SampleRequestListQuery["sampleRequests"]>;
  hasRowHover?: boolean;
  onTrackingIdClick?: (id: string) => void;
}

export const SampleRequestListDatagrid = ({
  sampleRequests,
  disabled,
  settings,
  onUpdateListSettings,
  onSort,
  sort,
  hasRowHover,
  onTrackingIdClick,
}: SampleRequestListDatagridProps) => {
  const location = useLocation();
  const intl = useIntl();
  const datagrid = useDatagridChangeState();
  const sampleRequestsLength = getSampleRequestRowsLength(sampleRequests, disabled);
  const ensureActionColumn = useCallback(
    (columns: string[]) =>
      columns.includes("action") ? columns : [...columns, "action"],
    [],
  );

  const selectedColumnsNormalized = useMemo(() => {
    const stored = settings?.columns ?? DEFAULT_COLUMNS;
    return ensureActionColumn(stored);
  }, [settings?.columns, ensureActionColumn]);

  const handleColumnChange = useCallback(
    picked => {
      const normalized = ensureActionColumn(picked.filter(Boolean));
      onUpdateListSettings("columns", normalized);
    },
    [ensureActionColumn, onUpdateListSettings],
  );
  const emptyColumn = useEmptyColumn();
  const memoizedStaticColumns = useMemo(
    () => sampleRequestListStaticColumnAdapter(emptyColumn, intl, sort, onTrackingIdClick),
    [emptyColumn, intl, sort, onTrackingIdClick],
  );
  const { handlers, staticColumns, visibleColumns, selectedColumns } = useColumns({
    gridName: "sample_request_list",
    staticColumns: memoizedStaticColumns,
    selectedColumns: selectedColumnsNormalized,
    onSave: handleColumnChange,
  });
  const handleHeaderClick = useCallback(
    (col: number) => {
      const { columnName, columnId } = getColumnNameAndId(visibleColumns[col].id);

      if (canBeSorted(columnName)) {
        onSort(columnName, columnId);
      }
    },
    [visibleColumns, onSort],
  );
  const getCellContent = useGetCellContent({
    columns: visibleColumns,
    sampleRequests,
    onTrackingIdClick,
  });

  return (
    <Box __marginTop={sampleRequestsLength > 0 ? -1 : 0}>
      <DatagridChangeStateContext.Provider value={datagrid}>
        <Datagrid
          readonly
          rowMarkers="none"
          loading={disabled}
          columnSelect="single"
          hasRowHover={hasRowHover}
          freezeColumns={2}
          verticalBorder={false}
          availableColumns={visibleColumns}
          onHeaderClicked={handleHeaderClick}
          emptyText={intl.formatMessage(messages.emptyText)}
          getCellContent={getCellContent}
          getCellError={() => false}
          menuItems={() => []}
          rows={getSampleRequestRowsLength(sampleRequests, disabled)}
          selectionActions={() => null}
          onColumnResize={handlers.onResize}
          onColumnMoved={handlers.onMove}
          renderColumnPicker={() => (
            <ColumnPicker
              staticColumns={staticColumns}
              selectedColumns={selectedColumns}
              onToggle={handlers.onToggle}
            />
          )}
          navigatorOpts={{ state: { from: location.pathname } }}
        />

        <Box paddingX={6}>
          <TablePaginationWithContext
            component="div"
            settings={settings}
            disabled={disabled}
            onUpdateListSettings={onUpdateListSettings}
          />
        </Box>
      </DatagridChangeStateContext.Provider>
    </Box>
  );
};
